import React, { useCallback, useEffect, useState } from 'react';
import axiosClient from '../../api/axiosClient';
import { ShieldAlert, Activity, Globe, Zap, Power, Radio, Target } from 'lucide-react';

const FleetWarMap = () => {
    const [pulse, setPulse] = useState({ total_nodes: 0, online_nodes: 0, active_threats: 0, captured_adversaries: 0 });
    const [threats, setThreats] = useState([]);
    const [, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        try {
            // 1. Fetch Aggregated Stats
            const pulseData = await axiosClient.get('/admin/sentinel/fleet/pulse');
            setPulse(pulseData);

            // 2. Fetch Map Coordinates (Agents & Threats)
            const mapData = await axiosClient.get('/admin/stats/threat-map');
            setThreats(mapData.threats || []);
        } catch (err) {
            console.error("War Map Fetch Failed", err);
        } finally {
            setLoading(false);
        }
    }, []);

    const triggerGlobalLockdown = async () => {
        if (window.confirm("🚨 CRITICAL: Activate GLOBAL FLEET LOCKDOWN? This will isolate every single node in the network.")) {
            try {
                const res = await axiosClient.post('/admin/sentinel/fleet/lockdown');
                alert(`OMEGA SWITCH ACTIVATED: ${res.affected_nodes} nodes isolated.`);
            } catch (err) { alert("Lockdown Command Failed."); }
        }
    };

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 15000);
        return () => clearInterval(interval);
    }, [fetchData]);

    return (
        <div className="p-6 bg-[#060816] min-h-screen text-white font-mono">
            {/* TOP KPI HUD */}
            <div className="mb-8 flex flex-col xl:flex-row xl:items-end xl:justify-between gap-5">
                <div>
                    <div className="inline-flex items-center gap-2 rounded-full border border-cyan-500/20 bg-cyan-500/10 px-4 py-2 text-[10px] font-black uppercase tracking-[0.22em] text-cyan-400">
                        <span className="h-2 w-2 rounded-full bg-cyan-400 animate-pulse" />
                        AIRS Strategic Fleet Grid
                    </div>
                    <h1 className="mt-5 text-3xl md:text-5xl font-black text-white tracking-tight">
                        Fleet War Map
                    </h1>
                    <p className="mt-3 text-sm text-slate-400 max-w-2xl">
                        Real-time operational view of sentinel nodes, active threats, adversary captures, and global containment readiness.
                    </p>
                </div>

                <div className="inline-flex w-fit items-center gap-3 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-emerald-400">
                    <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                    Live Sync · 15s
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                <StatCard icon={<Radio />} label="TOTAL SENTINELS" val={pulse.total_nodes} color="blue" />
                <StatCard icon={<Activity />} label="ONLINE NODES" val={pulse.online_nodes} color="green" />
                <StatCard icon={<ShieldAlert />} label="ACTIVE THREATS" val={pulse.active_threats} color="red" />
                <StatCard icon={<Target />} label="CAPTURED ADVERSARIES" val={pulse.captured_adversaries} color="yellow" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* GLOBAL MAP VISUALIZATION */}
                <div className="lg:col-span-3 bg-slate-950/70 border border-cyan-500/10 rounded-[2rem] h-[600px] relative overflow-hidden flex items-center justify-center shadow-2xl">
                    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent" />
                    <div className="absolute -left-32 top-1/3 h-72 w-72 rounded-full bg-cyan-500/10 blur-3xl" />
                    <div className="absolute -right-32 bottom-1/3 h-72 w-72 rounded-full bg-purple-500/10 blur-3xl" />
                    <Globe size={400} className="text-slate-800 opacity-20 absolute" />
                    
                    {/* Pulsing Fleet Nodes */}
                    <div className="z-10 text-center">
                        <div className="relative inline-block">
                            <div className="w-32 h-32 border-2 border-airs-500/20 rounded-full animate-ping absolute -inset-0"></div>
                            <div className="w-32 h-32 border border-airs-500/50 rounded-full flex items-center justify-center bg-airs-500/10 backdrop-blur-xl">
                                <Zap className="text-airs-400" size={48} />
                            </div>
                        </div>
                        <h2 className="mt-6 text-2xl font-bold tracking-widest text-slate-200">STRATEGIC WAR MAP</h2>
                        <p className="text-slate-500 text-sm">Monitoring {pulse.total_nodes} nodes across the sovereign grid.</p>
                    </div>

                    {/* Threat Overlays (Dynamic Markers) */}
                    {threats.map((t, i) => (
                        <div key={i} className="absolute flex flex-col items-center animate-bounce" 
                             style={{ top: `${Math.random() * 80}%`, left: `${Math.random() * 80}%` }}>
                            <div className="w-3 h-3 bg-red-500 rounded-full shadow-[0_0_15px_rgba(239,68,68,0.8)]"></div>
                            <span className="text-[8px] bg-black/80 px-1 mt-1 border border-red-500/50">{t.type}</span>
                        </div>
                    ))}
                </div>

                {/* COMMAND SIDEBAR */}
                <div className="space-y-6">
                    <div className="bg-slate-950/70 border border-red-500/20 p-5 rounded-[2rem] shadow-2xl">
                        <h3 className="text-xs font-bold text-slate-500 mb-4 tracking-widest uppercase">Global Operations</h3>
                        <button 
                            onClick={triggerGlobalLockdown}
                            className="w-full py-4 bg-red-600/10 border border-red-600/50 text-red-500 rounded-xl font-bold flex items-center justify-center gap-3 hover:bg-red-600 hover:text-white transition-all shadow-lg hover:shadow-red-600/20 group"
                        >
                            <Power size={20} className="group-hover:animate-spin" /> OMEGA LOCKDOWN
                        </button>
                        <p className="text-[10px] text-slate-600 mt-3 text-center italic">Requires Architect Authentication</p>
                    </div>

                    <div className="bg-slate-950/70 border border-cyan-500/10 p-5 rounded-[2rem] flex-1 shadow-2xl">
                        <h3 className="text-xs font-bold text-slate-500 mb-4 tracking-widest uppercase">Live Pulse Log</h3>
                        <div className="space-y-3 max-h-[350px] overflow-y-auto pr-2 custom-scrollbar">
                            {threats.slice(0, 10).map((t, idx) => (
                                <div key={idx} className="text-[10px] border-l-2 border-red-500 pl-3 py-1">
                                    <span className="text-slate-400 block">{new Date().toLocaleTimeString()}</span>
                                    <span className="text-red-400 font-bold uppercase">{t.type}</span>
                                    <span className="text-slate-500 block">Location: {t.city}, {t.country}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const STAT_THEME = {
    blue: {
        border: 'border-blue-500/20',
        iconBox: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
        value: 'text-blue-400',
        dot: 'bg-blue-400',
    },
    green: {
        border: 'border-emerald-500/20',
        iconBox: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
        value: 'text-emerald-400',
        dot: 'bg-emerald-400',
    },
    red: {
        border: 'border-red-500/20',
        iconBox: 'bg-red-500/10 text-red-400 border-red-500/20',
        value: 'text-red-400',
        dot: 'bg-red-400',
    },
    yellow: {
        border: 'border-yellow-500/20',
        iconBox: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
        value: 'text-yellow-400',
        dot: 'bg-yellow-400',
    },
};

const StatCard = ({ icon, label, val, color }) => {
    const theme = STAT_THEME[color] || STAT_THEME.blue;

    return (
        <div className={`group relative overflow-hidden rounded-[1.5rem] border ${theme.border} bg-slate-950/70 p-5 shadow-2xl hover:-translate-y-1 hover:bg-white/[0.045] transition-all duration-300`}>
            <div className="absolute -right-12 -top-12 h-32 w-32 rounded-full bg-white/[0.04] blur-2xl group-hover:bg-white/[0.07] transition-all" />

            <div className="relative flex items-center justify-between gap-4">
                <div className={`h-12 w-12 rounded-2xl border ${theme.iconBox} flex items-center justify-center`}>
                    {icon}
                </div>

                <span className={`h-2.5 w-2.5 rounded-full ${theme.dot} animate-pulse`} />
            </div>

            <div className="relative mt-5">
                <div className="text-[10px] text-slate-500 font-black uppercase tracking-[0.16em]">
                    {label}
                </div>
                <div className={`mt-2 text-3xl font-black tracking-tight ${theme.value} break-all`}>
                    {val}
                </div>
            </div>
        </div>
    );
};

export default FleetWarMap;